/**
 * Main Collection Filters JavaScript
 * Handles collection page interactivity including AJAX filtering and sorting.
 * RTL-first implementation.
 */

function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => { clearTimeout(timeout); func(...args); };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

class TzCollectionGrid {
    constructor(section) {
        this.section = section;
        this.sectionId = section.dataset.sectionId;
        if (!this.sectionId) {
            const sectionWrapper = section.closest('.shopify-section');
            if (sectionWrapper) {
                const idMatch = sectionWrapper.id.match(/shopify-section-(.+)/);
                this.sectionId = idMatch ? idMatch[1] : sectionWrapper.id;
            }
        }

        this.init();
    }

    init() {
        this.bindFilterHeaders();
        this.bindColorSwatches();
        this.bindCheckboxes();
        this.bindPriceInputs();
        this.bindSortGroup();
        this.bindFilterToggle();
        this.bindLoadMore();
        this.translateSortOptions();

        this.updateFilterStates();

        const productGrid = this.section.querySelector('.main-collection__grid');
        if (productGrid) {
            this.applyAllFilters(productGrid);
        }
    }

    bindFilterHeaders() {
        if (this.headerListener) this.section.removeEventListener('click', this.headerListener);
        this.headerListener = (e) => {
            const header = e.target.closest('.tz-collection-filter-header');
            if (header) {
                e.preventDefault();
                const parent = header.closest('.tz-collection-sidebar-section');
                if (!parent) return;
                const content = parent.querySelector('.tz-collection-checkbox-group, .tz-collection-price-range, .tz-collection-color-grid');
                const icon = header.querySelector('.tz-collection-filter-icon');

                if (content) {
                    const isHidden = content.style.display === 'none';
                    content.style.display = isHidden ? 'block' : 'none';
                    if (icon) icon.textContent = isHidden ? 'remove' : 'add';
                }
            }
        };
        this.section.addEventListener('click', this.headerListener);
    }

    bindColorSwatches() {
        if (this.colorListener) this.section.removeEventListener('click', this.colorListener);
        this.colorListener = (e) => {
            const swatch = e.target.closest('.tz-collection-color-swatch');
            if (swatch) {
                e.preventDefault();
                swatch.classList.toggle('is-active');
                swatch.setAttribute('aria-checked', swatch.classList.contains('is-active'));
                this.updateFilters();
            }
        };
        this.section.addEventListener('click', this.colorListener);
    }

    bindCheckboxes() {
        if (this.checkboxListener) this.section.removeEventListener('change', this.checkboxListener);
        this.checkboxListener = (e) => {
            const checkbox = e.target.closest('.tz-collection-checkbox');
            if (checkbox) {
                const label = checkbox.closest('.tz-collection-checkbox-label');
                if (label) label.setAttribute('aria-checked', checkbox.checked);
                this.updateFilters();
            }
        };
        this.section.addEventListener('change', this.checkboxListener);
    }

    bindPriceInputs() {
        if (this.priceListener) this.section.removeEventListener('input', this.priceListener);
        const update = debounce(() => this.updateFilters(), 500);

        this.priceListener = (e) => {
            const input = e.target.closest('.tz-collection-price-input');
            if (input) {
                if (input.type === 'range') {
                    const type = input.dataset.type;
                    const numInput = this.section.querySelector(`.tz-collection-price-input[data-type="${type}"][type="number"]`);
                    if (numInput) numInput.value = input.value;
                    this.updateSliderTrack(input);
                } else if (input.type === 'number') {
                    const type = input.dataset.type;
                    const rangeInput = this.section.querySelector(`.tz-collection-price-input[data-type="${type}"][type="range"]`);
                    if (rangeInput) {
                        rangeInput.value = input.value;
                        this.updateSliderTrack(rangeInput);
                    }
                }
                update();
            }
        };
        this.section.addEventListener('input', this.priceListener);

        this.section.querySelectorAll('.tz-collection-price-input[type="range"]').forEach(input => this.updateSliderTrack(input));
    }

    updateSliderTrack(input) {
        const min = parseFloat(input.min) || 0;
        const max = parseFloat(input.max) || 100;
        const val = ((parseFloat(input.value) - min) / (max - min)) * 100;
        const dir = document.documentElement.dir === 'rtl' ? 'left' : 'right';
        input.style.background = `linear-gradient(to ${dir}, #000 ${val}%, #e5e5e5 ${val}%)`;
    }

    bindSortGroup() {
        if (this.sortListener) this.section.removeEventListener('change', this.sortListener);
        this.sortListener = (e) => {
            const select = e.target.closest('.tz-collection-sort-select');
            if (select) {
                const val = select.tagName === 'SELECT' ? select.value : select.querySelector('select').value;
                this.updateFilters({ sort: val });
            }
        };
        this.section.addEventListener('change', this.sortListener);
    }

    bindFilterToggle() {
        const toggleBtn = this.section.querySelector('.tz-collection-filter-toggle-btn');
        const sidebar = this.section.querySelector('.tz-collection-sidebar');
        if (!toggleBtn || !sidebar) return;

        toggleBtn.onclick = (e) => {
            e.preventDefault();
            const isVisible = sidebar.classList.toggle('is-visible');
            toggleBtn.setAttribute('aria-expanded', isVisible);
            
            // On desktop, it might also toggle 'is-hidden' if we want manual collapse there too
            sidebar.classList.toggle('is-hidden');
        };
    }

    bindLoadMore() { }

    translateSortOptions() { }

    updateFilterStates() {
        const params = new URLSearchParams(window.location.search);

        // Colors
        const activeColors = params.getAll('filter.v.option.color').concat(params.getAll('filter.v.option.צבע'));
        this.section.querySelectorAll('.tz-collection-color-swatch').forEach(s => {
            const val = s.dataset.value;
            if (activeColors.includes(val)) {
                s.classList.add('is-active');
                s.setAttribute('aria-checked', 'true');
            } else {
                s.classList.remove('is-active');
                s.setAttribute('aria-checked', 'false');
            }
        });

        // Hide Size filter if out of stock is checked
        const availParams = params.getAll('availability');
        const isOutOfStock = availParams.includes('out_of_stock') || params.getAll('filter.v.availability').includes('0');
        const sizeFilterSec = this.section.querySelector('.tz-size-filter-section');
        if (sizeFilterSec) {
            sizeFilterSec.style.display = isOutOfStock ? 'none' : '';
        }

        // Checkboxes
        this.section.querySelectorAll('.tz-collection-checkbox').forEach(c => {
            const val = c.value.toLowerCase();
            const name = c.name;

            let isActive = false;
            
            // Case-insensitive check for all variations
            isActive = params.getAll(name).some(v => v.toLowerCase() === val) ||
                params.getAll('filter.p.' + name).some(v => v.toLowerCase() === val) ||
                (name === 'availability' && (
                    params.getAll('availability').some(v => v.toLowerCase() === val) ||
                    (val === 'in_stock' && params.getAll('filter.v.availability').includes('1')) ||
                    (val === 'out_of_stock' && params.getAll('filter.v.availability').includes('0'))
                ));

            c.checked = isActive;
            const label = c.closest('.tz-collection-checkbox-label');
            if (label) label.setAttribute('aria-checked', isActive);
        });

        // Prices
        const min = params.get('filter.v.price.gte');
        const max = params.get('filter.v.price.lte');
        if (min) {
            const input = this.section.querySelector('.tz-collection-price-input[data-type="min"]');
            if (input) input.value = min;
        }
        if (max) {
            const input = this.section.querySelector('.tz-collection-price-input[data-type="max"]');
            if (input) input.value = max;
        }

        // Generic S&D filters: restore any checkbox whose name is a filter.* param in the URL
        // This covers all dynamic filters from the Search & Discovery app.
        this.section.querySelectorAll('.tz-collection-checkbox').forEach(c => {
            const name = c.name;
            // Skip already-handled hardcoded filter names
            if (['availability', 'product_type'].includes(name)) return;
            // Skip color swatches (handled separately) and size (handled via tz-size-checkbox)
            if (name.startsWith('filter.v.option.') && 
                (c.classList.contains('tz-collection-color-swatch') || c.classList.contains('tz-size-checkbox'))) return;

            const urlValues = params.getAll(name);
            const isActive = urlValues.some(v => v.toLowerCase() === c.value.toLowerCase());
            c.checked = isActive;
            const label = c.closest('.tz-collection-checkbox-label');
            if (label) label.setAttribute('aria-checked', isActive);
        });
    }

    updateFilters(options = {}) {
        this.showLoadingState();

        const params = new URLSearchParams(window.location.search);
        const keysToKeep = ['q', 'type', 'view', 'sort_by'];
        const keysToClear = Array.from(params.keys()).filter(key =>
            !keysToKeep.includes(key) || key.startsWith('filter.')
        );
        keysToClear.forEach(key => params.delete(key));

        if (options.sort) {
            params.set('sort_by', options.sort);
        } else {
            let sortSelect = this.section.querySelector('.tz-collection-sort-select');
            if (sortSelect) {
                const native = sortSelect.tagName === 'SELECT' ? sortSelect : sortSelect.querySelector('select');
                if (native) params.set('sort_by', native.value);
            }
        }

        // Availability
        const availability = Array.from(this.section.querySelectorAll('.tz-collection-checkbox[name="availability"]:checked')).map(c => c.value);
        availability.forEach(v => {
            params.append('availability', v);
            params.append('filter.v.availability', v === 'in_stock' ? '1' : '0');
        });

        // Colors
        const colors = Array.from(this.section.querySelectorAll('.tz-collection-color-swatch.is-active')).map(s => s.dataset.value);
        colors.forEach(c => {
            params.append('filter.v.option.color', c);
            params.append('filter.v.option.צבע', c);
        });

        // remove pagination
        params.delete('page');

        // remove existing size filters
        this.section.querySelectorAll('.tz-size-checkbox').forEach(cb => {
            params.delete(cb.name);
        });

        // append selected sizes
        let checkedSizes = Array.from(this.section.querySelectorAll('.tz-size-checkbox:checked'));
        
        // If out_of_stock is selected, ignore any currently checked sizes so they don't conflict
        if (availability.includes('out_of_stock')) {
            checkedSizes = [];
        }

        checkedSizes.forEach(cb => {
            params.append(cb.name, cb.value);
        });

        // optional availability enforcement
        if (checkedSizes.length > 0 && !params.has('filter.v.availability')) {
            params.append('filter.v.availability', '1');
        }

        // Product Type
        const types = Array.from(this.section.querySelectorAll('.tz-collection-checkbox[name="product_type"]:checked')).map(c => c.value);
        types.forEach(v => params.append('filter.p.product_type', v));

        // Prices
        const minInput = this.section.querySelector('.tz-collection-price-input[data-type="min"][type="number"]');
        const maxInput = this.section.querySelector('.tz-collection-price-input[data-type="max"][type="number"]');
        const rangeMax = this.section.querySelector('.tz-collection-price-input[data-type="max"][type="range"]');

        const minVal = minInput ? parseFloat(minInput.value) : 0;
        const maxVal = maxInput ? parseFloat(maxInput.value) : 0;
        const absMax = rangeMax ? parseFloat(rangeMax.getAttribute('max')) : 0;

        if (!isNaN(minVal) && minVal > 0) params.set('filter.v.price.gte', Math.round(minVal));
        if (!isNaN(maxVal) && maxVal > 0 && (absMax === 0 || maxVal < absMax)) params.set('filter.v.price.lte', Math.round(maxVal));

        // Generic catch-all: collect any other checked S&D filter checkboxes
        // that are not already covered by the hardcoded blocks above.
        // This makes ALL S&D filters work automatically, matching Horizon's form submission.
        const handledNames = new Set(['availability', 'product_type']);
        this.section.querySelectorAll('.tz-size-checkbox, .tz-collection-color-swatch').forEach(el => {
            if (el.name) handledNames.add(el.name);
        });

        this.section.querySelectorAll('.tz-collection-checkbox:checked').forEach(cb => {
            if (!handledNames.has(cb.name)) {
                // Use the exact param_name from the Shopify filter object (set as name attr in Liquid)
                params.append(cb.name, cb.value);
            }
        });

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        this.fetchProducts(newUrl);
    }

    async fetchProducts(url) {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response not ok');
            const text = await response.text();
            const html = new DOMParser().parseFromString(text, 'text/html');

            const newGrid = html.querySelector('.main-collection__grid');
            const currentGrid = this.section.querySelector('.main-collection__grid');
            if (newGrid && currentGrid) {
                currentGrid.innerHTML = newGrid.innerHTML;
                window.history.pushState({}, '', url);
                this.applyAllFilters(currentGrid);
                initQuickAddButtons(this.section);
            }

            const newSidebar = html.querySelector('.tz-collection-sidebar');
            const currentSidebar = this.section.querySelector('.tz-collection-sidebar');
            if (newSidebar && currentSidebar) {
                currentSidebar.innerHTML = newSidebar.innerHTML;
                this.updateFilterStates();
                this.bindFilterHeaders();
                this.bindColorSwatches();
                this.bindCheckboxes();
                this.bindPriceInputs();
            }

        } catch (e) {
            console.error('TzCollectionGrid: AJAX Fetch failed', e);
        } finally {
            this.hideLoadingState();
        }
    }

    applyAllFilters(grid) {
        if (!grid) return;
        const products = grid.querySelectorAll('.tz-collection-product-item');

        // 1. Reset all
        products.forEach(item => item.style.display = '');

        // 2. Read current filter state
        const availability = Array.from(this.section.querySelectorAll('.tz-collection-checkbox[name="availability"]:checked')).map(c => c.value);
        const colors = Array.from(this.section.querySelectorAll('.tz-collection-color-swatch.is-active')).map(s => s.dataset.value.toLowerCase());
        const types = Array.from(this.section.querySelectorAll('.tz-collection-checkbox[name="product_type"]:checked')).map(c => c.value.toLowerCase());
        const sizes = Array.from(this.section.querySelectorAll('.tz-size-checkbox:checked')).map(c => c.value.toLowerCase());

        const minPrice = parseFloat(this.section.querySelector('.tz-collection-price-input[data-type="min"][type="number"]')?.value) || 0;
        const maxPriceInput = this.section.querySelector('.tz-collection-price-input[data-type="max"][type="number"]');
        const maxPrice = parseFloat(maxPriceInput?.value) || 0;
        const absMax = parseFloat(this.section.querySelector('.tz-collection-price-input[data-type="max"][type="range"]')?.getAttribute('max')) || 0;


        products.forEach(item => {
            let show = true;

            // Stock filter - Standard inclusion logic
            if (availability.length > 0) {
                const isInStock = item.dataset.hasInStockVariant === 'true';
                const isOutOfStock = item.dataset.hasOutOfStockVariant === 'true';

                let matches = false;
                if (availability.includes('in_stock') && isInStock) matches = true;
                if (availability.includes('out_of_stock') && isOutOfStock) matches = true;

                if (!matches) show = false;
            }

            // Price filter
            if (show) {
                const itemPrice = parseFloat(item.dataset.price) || 0;
                if (minPrice > 0 && itemPrice < minPrice) show = false;
                if (maxPrice > 0 && maxPrice < absMax && itemPrice > maxPrice) show = false;
            }

            // Color filter
            if (show && colors.length > 0) {
                const itemColors = (item.dataset.productColors || '').toLowerCase().split(',').map(c => c.trim());
                if (!colors.some(c => itemColors.includes(c))) show = false;
            }
            
            // Size filter
            if (show && sizes.length > 0 && item.dataset.productSizes !== undefined) {
                const itemSizes = (item.dataset.productSizes || '').toLowerCase().split(',').map(s => s.trim());
                if (!sizes.some(s => itemSizes.includes(s))) show = false;
            }

            // Type filter
            if (show && types.length > 0) {
                const itemType = (item.dataset.productType || '').toLowerCase().trim();
                const normalizedTypes = types.map(t => t.toLowerCase().trim());

                // Lenient matching: Allow exact match OR if the filter value is a substring of the product type
                // Crucial: itemType must NOT be empty to match via inclusion
                const isMatch = normalizedTypes.some(filterType =>
                    itemType === filterType ||
                    (itemType !== '' && (itemType.includes(filterType) || filterType.includes(itemType)))
                );

                if (!isMatch) show = false;
            }

            item.style.display = show ? '' : 'none';
        });

        this.updateProductCount(grid);
    }

    updateProductCount(grid) {
        const visibleCount = Array.from(grid.querySelectorAll('.tz-collection-product-item')).filter(i => i.style.display !== 'none').length;
        const countEl = this.section.querySelector('.tz-collection-product-count');
        if (countEl) {
            const template = countEl.dataset.template || '%s מוצרים';
            countEl.textContent = template.replace('%s', visibleCount);
        }
    }

    showLoadingState() {
        const grid = this.section.querySelector('.main-collection__grid');
        if (grid) { grid.style.opacity = '0.5'; grid.style.pointerEvents = 'none'; }
    }

    hideLoadingState() {
        const grid = this.section.querySelector('.main-collection__grid');
        if (grid) { grid.style.opacity = '1'; grid.style.pointerEvents = 'auto'; }
    }
}

function initQuickAddButtons(section) {
    section.querySelectorAll('.tz-collection-quick-add-btn').forEach(btn => {
        btn.onclick = (e) => {
            e.preventDefault();
            const id = btn.dataset.variantId;
            if (id) {
                fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
                }).then(() => document.dispatchEvent(new CustomEvent('cart:update')));
            }
        };
    });
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-section-type="tz-collection-grid"]').forEach(s => new TzCollectionGrid(s));
});

document.addEventListener('shopify:section:load', (e) => {
    const s = e.target.querySelector('[data-section-type="tz-collection-grid"]');
    if (s) new TzCollectionGrid(s);
});
